import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import {
  getRequest,
  putRequest,
  putWithFormData,
} from "../../helpers/api.helper";

const Step3Edit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};

  // Refs for search inputs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef2 = useRef<HTMLInputElement>(null);

  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [modalData2, setModalData2] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTerm2, setSearchTerm2] = useState("");

  const [castes, setCastes] = useState<any[]>([]);
  const [subCastes, setSubCastes] = useState<any[]>([]);
  const [raasi, setRaasi] = useState<any[]>([]);
  const [star, setStar] = useState<any[]>([]);

  const [selectedOptions, setSelectedOptions] = useState<any>({
    Languages_Known: profile?.languagesKnown || [],
  });

  const [selectedOptions2, setSelectedOptions2] = useState<any>({
    religion: profile?.religion
      ? { value: profile.religion, label: profile.religion }
      : null,
    Mother_Tongue: profile?.motherTongue
      ? { value: profile.motherTongue, label: profile.motherTongue }
      : null,
    caste: profile?.caste
      ? { value: profile.caste, label: profile.caste }
      : null,
    subcaste: profile?.subcaste
      ? { value: profile.subcaste, label: profile.subcaste }
      : null,
    raasi: profile?.raasi
      ? { value: profile.raasi, label: profile.raasi }
      : null,
    star: profile?.star ? { value: profile.star, label: profile.star } : null,
    gotram: profile?.gotram
      ? { value: profile.gotram, label: profile.gotram }
      : null,
  });

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    Languages_Known: [
      "Bengali",
      "English",
      "Hindi",
      "Kannada",
      "Marathi",
      "Tamil",
      "Telugu",
    ].map((lang) => ({ value: lang.toLowerCase(), label: lang })),
  };

  const dropdownOptions2: any = {
    religion: ["Buddhist", "Christian", "Hindu", "Muslim", "Sikh"].map((r) => ({
      value: r.toLowerCase(),
      label: r,
    })),
    Mother_Tongue: [
      "Bengali",
      "English",
      "Hindi",
      "Kannada",
      "Marathi",
      "Tamil",
      "Telugu",
    ].map((m) => ({ value: m.toLowerCase(), label: m })),
    caste: castes.map((c) => ({ value: c.caste, label: c.caste })),
    subcaste: subCastes.map((sc) => ({
      value: sc.sub_caste,
      label: sc.sub_caste,
    })),
    raasi: raasi.map((r) => ({ value: r.raasi, label: r.raasi })),
    star: star.map((s) => ({ value: s.star, label: s.star })),
  };

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchCommonData = async () => {
      try {
        const response = await getRequest("/common/all");
        if (response.success) {
          setStar(response.data.star || []);
          setCastes(response.data.caste || []);
          setSubCastes(response.data.subCaste || []);
          setRaasi(response.data.raasi || []);
        }
      } catch (error) {
        console.error("Error fetching common profile data:", error);
      }
    };
    fetchCommonData();
  }, []);

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (modalData && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData2 && searchInputRef2.current) {
      setTimeout(() => searchInputRef2.current?.focus(), 100);
    }
  }, [modalData2]);

  /** ---------- Handlers ---------- **/
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: any,
  ) => {
    const { checked } = e.target;
    setSelectedOptions((prev: any) => ({
      ...prev,
      Languages_Known: checked
        ? [...prev.Languages_Known, option.value]
        : prev.Languages_Known.filter((lang: any) => lang !== option.value),
    }));
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setSelectedOptions((prev: any) => ({
      ...prev,
      Languages_Known: checked
        ? dropdownOptions.Languages_Known.map((o: any) => o.value)
        : [],
    }));
  };

  const handleSelect = (option: any) => {
    setSelectedOptions2((prev: any) => ({ ...prev, [modalData2.key]: option }));
    closeModal2();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formattedOptions = {
      religion: selectedOptions2.religion?.value || "",
      motherTongue: selectedOptions2.Mother_Tongue?.value || "",
      caste: selectedOptions2.caste?.value || "",
      subcaste: selectedOptions2.subcaste?.value || "",
      raasi: selectedOptions2.raasi?.value || "",
      star: selectedOptions2.star?.value || "",
      gotram: selectedOptions2.gotram?.value || "",
    };

    const payload = {
      ...formattedOptions,
      languagesKnown: selectedOptions.Languages_Known,
      step1: 1,
      step2: 1,
      step3: 1,
    };
    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        payload,
      );
      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error: any) {
      console.error("Error updating details:", error.message);
    }
  };

  const openModal = (key: string) => {
    setSearchTerm("");
    setModalData({ key, options: dropdownOptions[key] });
  };

  const openModal2 = (key: string) => {
    setSearchTerm2("");
    setModalData2({ key, options: dropdownOptions2[key] });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
    // Blur any active element to hide keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const closeModal2 = () => {
    setModalData2(null);
    setSearchTerm2("");
    // Blur any active element to hide keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleBackClick = () => navigate(-1);

  /** ---------- Reusable Modals ---------- **/
  const MultiSelectModal = () =>
    modalData && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key}
          </h2>

          {/* Select All */}
          <div className="flex items-center space-x-2 mb-4">
            <input
              id="selectAllCheckbox"
              type="checkbox"
              checked={
                selectedOptions.Languages_Known.length ===
                dropdownOptions.Languages_Known.length
              }
              onChange={handleSelectAllChange}
              className="form-checkbox h-4 w-4 text-red-600"
            />
            <label className="block text-gray-700">Select All</label>
          </div>

          {/* Search */}
          <input
            ref={searchInputRef}
            type="text"
            placeholder={`Search ${modalData.key}`}
            className="w-full px-4 py-2 mb-4 border rounded focus:ring focus:ring-blue-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
          />

          {/* Options */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {modalData.options
              .filter((option: any) =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .map((option: any) => (
                <React.Fragment key={option.value}>
                  <div className="flex items-center space-x-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedOptions.Languages_Known.includes(
                        option.value,
                      )}
                      onChange={(e) => handleCheckboxChange(e, option)}
                      className="form-checkbox h-4 w-4 text-red-600"
                    />
                    <label className="block text-gray-700">
                      {option.label}
                    </label>
                  </div>
                  <hr className="border-gray-100" />
                </React.Fragment>
              ))}
          </div>

          <button
            className="mt-4 w-full bg-red-500 text-white py-2 rounded hover:bg-red-600"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );

  const SingleSelectModal = () =>
    modalData2 && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData2.key}
          </h2>

          <input
            ref={searchInputRef2}
            type="text"
            placeholder={`Search ${modalData2.key}`}
            className="w-full px-4 py-2 mb-4 border rounded focus:ring focus:ring-blue-200"
            value={searchTerm2}
            onChange={(e) => setSearchTerm2(e.target.value)}
            autoComplete="off"
          />

          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {modalData2.options
              .filter((option: any) =>
                option.label.toLowerCase().includes(searchTerm2.toLowerCase()),
              )
              .map((option: any) => (
                <React.Fragment key={option.value}>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </button>
                  <hr className="border-gray-100" />
                </React.Fragment>
              ))}
          </div>

          <button
            className="mt-4 w-full bg-red-500 text-white py-2 rounded hover:bg-red-600"
            onClick={closeModal2}
          >
            Continue
          </button>
        </div>
      </div>
    );

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-red-100">
      <div className="w-full max-w-4xl bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full fixed top-0 bg-red-600 p-3 flex items-center justify-center z-50">
          <button onClick={handleBackClick} className="absolute left-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-white">
            Step 3 - Religion & Caste
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 mt-20 py-6 px-4">
          {/* Multi Select Section */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key} className="space-y-1">
              <label className="text-red-500 font-medium capitalize">
                {key}
              </label>
              <div
                className="border rounded-lg px-3 py-3 flex justify-between items-center cursor-pointer hover:shadow"
                onClick={() => openModal(key)}
              >
                <span className="text-gray-600">
                  {selectedOptions[key]?.length
                    ? dropdownOptions[key]
                        .filter((opt: any) =>
                          selectedOptions[key].includes(opt.value),
                        )
                        .map((opt: any) => opt.label)
                        .join(", ")
                    : `Select ${key}`}
                </span>
                <RiArrowRightSLine className="text-gray-500 text-2xl" />
              </div>
            </div>
          ))}

          {/* Single Select Section */}
          {Object.keys(dropdownOptions2).map((key) => (
            <div key={key} className="space-y-1">
              <label className="text-red-500 font-medium capitalize">
                {key}
              </label>
              <div
                className="border rounded-lg px-3 py-3 flex justify-between items-center cursor-pointer hover:shadow"
                onClick={() => openModal2(key)}
              >
                <span className="text-gray-600">
                  {selectedOptions2[key]?.label || `Select ${key}`}
                </span>
                <RiArrowRightSLine className="text-gray-500 text-2xl" />
              </div>
            </div>
          ))}

          <div className="space-y-1">
            <label className="text-red-500 font-medium capitalize">
              Gotram
            </label>

            <input
              type="text"
              placeholder="Enter Gotram"
              value={selectedOptions2.gotram?.value || ""}
              onChange={(e) =>
                setSelectedOptions2((prev: any) => ({
                  ...prev,
                  gotram: {
                    value: e.target.value,
                    label: e.target.value,
                  },
                }))
              }
              className="w-full border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
          >
            Update Religion and Caste Details
          </button>
        </form>
      </div>

      {/* Modals */}
      <MultiSelectModal />
      <SingleSelectModal />
    </div>
  );
};

export default Step3Edit;
